// ── BCHNostr — discover all business profiles (kind 30499) ────────
//
// Reads business profiles from relays for the directory. This is public
// UGC, so the *list* is unfiltered by author — but each profile is parsed
// per-event and any trust badge is computed separately (never inferred
// from mere presence). Replaceable events are collapsed per author,
// newest wins, and retired profiles (empty content) are dropped.

import { useMemo } from 'react';
import { useNostrQuery } from './useNostrQuery';
import {
  BUSINESS_KIND, BUSINESS_TAG, parseBusiness, type BusinessProfile,
} from '../lib/business';

export interface UseBusinessDirectoryResult {
  businesses: BusinessProfile[];
  loading: boolean;
  refetch: () => void;
}

export function useBusinessDirectory(category?: string): UseBusinessDirectoryResult {
  const filters = useMemo(() => {
    const t = category && category !== 'all'
      ? [BUSINESS_TAG, `bch-biz-cat-${category}`]
      : [BUSINESS_TAG];
    return [{ kinds: [BUSINESS_KIND], '#t': t, limit: 200 }];
  }, [category]);

  const { events, loading, refetch } = useNostrQuery({ filters, allRelays: true });

  const businesses = useMemo(() => {
    // Collapse to newest per author (30499 is replaceable).
    const byAuthor = new Map<string, typeof events[number]>();
    for (const ev of events) {
      const existing = byAuthor.get(ev.pubkey);
      if (!existing || ev.created_at > existing.created_at) byAuthor.set(ev.pubkey, ev);
    }
    return [...byAuthor.values()]
      .map(parseBusiness)
      .filter((b): b is BusinessProfile => b !== null)
      .sort((a, b) => b.created_at - a.created_at);
  }, [events]);

  return { businesses, loading, refetch };
}
